import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import Seo from "../components/site/Seo";
import { ArrowRight } from "lucide-react";

// Strip HTML tags + decode entities for plain-text card previews.
const stripHtml = (html) => {
  if (!html) return "";
  if (typeof document !== "undefined") {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim();
  }
  return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const { settings } = useSettings();

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="products-page">
      <Seo
        title="Products — Reptile Global"
        description="Explore our suite of SaaS products built to scale modern businesses."
        organizationSchema={settings?.organization_schema}
      />
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-3">Products</div>
            <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter text-zinc-950">
              A full-stack <span className="text-orange-500">software suite</span> for modern businesses.
            </h1>
            <p className="mt-6 text-lg text-zinc-600">Pick a product, or use them together — they're built to play nice.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {products.map((p) => {
              const isGreen = p.color === "green";
              return (
                <Link
                  key={p.id}
                  to={`/products/${p.slug}`}
                  data-testid={`product-card-${p.slug}`}
                  className="group bg-white border border-zinc-200 rounded-2xl p-8 hover:-translate-y-1 hover:shadow-xl hover:shadow-orange-500/10 transition-all duration-300"
                >
                  <div className="flex items-center justify-between mb-5">
                    <span className={`text-xs uppercase tracking-[0.18em] font-bold px-3 py-1 rounded-full ${isGreen ? "text-green-700 bg-green-50" : "text-orange-700 bg-orange-50"}`}>
                      {p.category}
                    </span>
                    <ArrowRight className="text-zinc-400 group-hover:text-orange-500 group-hover:translate-x-1 transition" size={18} />
                  </div>
                  <h3 className="font-display text-3xl font-bold text-zinc-900 mb-2">{p.name}</h3>
                  <p className="text-zinc-600 text-base leading-relaxed mb-4">{p.tagline}</p>
                  <p
                    className="text-zinc-500 text-sm leading-relaxed line-clamp-2"
                    data-testid={`product-short-${p.slug}`}
                  >
                    {stripHtml(p.short_description)}
                  </p>
                  <div className={`h-1 rounded-full mt-6 w-12 group-hover:w-24 transition-all ${isGreen ? "bg-green-500" : "bg-orange-500"}`} />
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
